import './polyfills.js'
import { getElementTag } from '@nrk/dh-server-libs/get-element-tag'
import introscroll from './components/introscroll.js'
import spesialmodul from './components/media/spesialmodul.js'
import stegvisVideo from '@nrk/dh-basis-stegvis-video'
import levendeStillbilde from '@nrk/dh-basis-levende-stillbilde'
import { toArray } from '@nrk/dh-basis-common'
import { createTracker } from '@nrk/dh-snowplow'
import { EventSchema, ViewportEventKind } from '@nrk/snowplow-web'

import styles from './components/introscroll.module.css'
import './components/cards/alt-text.module.css'
import './client.module.css'

const tag = getElementTag(__APP_NAME__, __APP_VERSION__)

function safeDefine(elementName, constructor) {
  if (typeof customElements.get(elementName) === 'undefined') {
    customElements.define(elementName, constructor)
  }
}

export class AppElement extends HTMLElement {
  name = tag
  #tracker
  #entryObserver
  #exitObserver

  connectedCallback() {
    this.initCustomElements()

    this.#createTracker()
    this.#startViewportLogger()
    this.addEventListener('dh-basis-scrollhistorie-trigger-activated', this.#handleTriggerActivated)
  }

  disconnectedCallback() {
    this.#stopViewportLogger()
    this.removeEventListener(
      'dh-basis-scrollhistorie-trigger-activated',
      this.#handleTriggerActivated,
    )
  }

  async initCustomElements() {
    safeDefine('dh-introscroll', introscroll)
    safeDefine('dh-ints-basis-stegvis-video', stegvisVideo)
    safeDefine('dh-ints-basis-levende-stillbilde', levendeStillbilde)
    safeDefine('dh-ints-spesialmodul', spesialmodul)
    if (
      this.features.lottie &&
      typeof customElements.get('dh-ints-basis-stegvis-lottie') === 'undefined'
    ) {
      const stegvisLottie = (
        await import('@nrk/dh-basis-stegvis-lottie' /* webpackChunkName: "dh-stegvis-lottie" */)
      ).default

      safeDefine('dh-ints-basis-stegvis-lottie', stegvisLottie)
    }

    if (this.features.kart && typeof customElements.get('dh-ints-kart') === 'undefined') {
      const kart = (
        await import('./lib/karthistorie-chunk.js' /* webpackChunkName: "dh-karthistorie" */)
      ).default

      safeDefine('dh-ints-kart', kart)
    }

    if (location.hostname.startsWith('preview') || location.hostname.startsWith('localhost')) {
      toArray(this.querySelectorAll(`.${styles.introscroll__error}`)).forEach((error) => {
        error.style.display = 'block'
      })
    }
  }

  #createTracker() {
    if (window.preventTracking) {
      return
    }

    if (process.env.DEV) {
      import('@nrk/dh-snowplow/mock').then(({ mock }) => mock()).catch(console.error)
    }

    this.#tracker = createTracker('dh-introscroll', {
      environment: process.env.ENVIRONMENT,
      contentContext: {
        source: 'pp',
        id: this.dataset.selfId,
      },
    })
  }

  #startViewportLogger() {
    if (window.preventTracking) {
      return
    }

    this.#entryObserver = new IntersectionObserver((entries) => {
      if (!entries[0]?.isIntersecting) {
        return
      }
      this.#entryObserver?.disconnect()
      this.#exitObserver?.observe(this)
      this.#tracker?.trackSelfDescribingEvent({
        schema: EventSchema.ViewportEvent,
        data: {
          kind: ViewportEventKind.Entry,
        },
      })
    }, {})

    this.#exitObserver = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) {
          return
        }
        this.#exitObserver?.disconnect()
        this.#tracker?.trackSelfDescribingEvent({
          schema: EventSchema.ViewportEvent,
          data: {
            kind: ViewportEventKind.Exit,
          },
        })
      },
      {
        threshold: [1],
        // `margin-block: 99000px -100%` setter opp et rektangel over skjermen
        // `margin-inline: 10%` sikrer at komponenter som stikker litt ut på høyre og venstre side trigger threshold på 1
        rootMargin: '99000px 10% -100% 10%',
      },
    )

    this.#entryObserver.observe(this)
  }

  #handleTriggerActivated = (e) => {
    this.#tracker?.trackSelfDescribingEvent({
      schema: EventSchema.StepEvent,
      data: {
        current: e.detail.triggerIndex + 1,
        total: this.cards.length,
      },
    })
  }

  #stopViewportLogger() {
    this.#entryObserver?.disconnect()
    this.#exitObserver?.disconnect()
  }

  get cards() {
    return this.querySelectorAll('[data-show-background]')
  }

  get features() {
    return JSON.parse(this.getAttribute('features'))
  }
}

safeDefine(tag, AppElement)
